/**
 * QueBoard Tutorial Module Definitions
 *
 * 기능별 대표 문제의 첫 번째 문제로 각각 독립적인 튜터리얼 구성.
 * - teleport10: 텔레포트(10) — 6×9, →10 + 자동 줄십
 * - teleport5: 텔레포트(5) — 6×7, →5 + 수동 뚱십
 * - fatTie: 뚱십 묶기 — 7×4, 직접 2×5 묶기
 * - bank: 은행 — 7×3, 빌리기 → 놓기 → 반납 → 묶기
 */
import type { Problem, TutorialModule, TutorialModuleId } from './types'

// ============================================================
// teleport10 — 텔레포트(10): 6×9=54
// →10으로 줄십 자동 묶기를 배운다
// ============================================================
const teleport10Module: TutorialModule = {
  id: 'teleport10',
  name: '텔레포트(10)',
  description: '→10 텔레포트로 줄십(1×10) 자동 묶기',
  problem: { left: 6, right: 9 },
  steps: [
    {
      id: 'tp10-1',
      instruction: '표시등에서 "십"을 눌러 가로 10줄로 바꿔보세요!',
      hint: '초록색 "십" 표시등을 탭하세요',
      highlightArea: { type: 'teleport-btn', buttonId: 'row10' },
      validate: (state) => state.completed,
    },
  ],
}

// ============================================================
// teleport5 — 텔레포트(5): 6×7=42
// →5로 변환 후 뚱십(2×5)을 수동 묶기하는 법을 배운다
// ============================================================
const teleport5Module: TutorialModule = {
  id: 'teleport5',
  name: '텔레포트(5)',
  description: '→5 텔레포트로 뚱십(2×5) 수동 묶기',
  problem: { left: 6, right: 7 },
  steps: [
    {
      id: 'tp5-1',
      instruction: '표시등에서 "5"를 눌러 가로 5줄로 바꿔보세요!',
      hint: '초록색 "5" 표시등을 탭하세요',
      highlightArea: { type: 'teleport-btn', buttonId: 'row5' },
      validate: (state) => {
        for (let c = 0; c < 10; c++) {
          for (let r = 6; r < 10; r++) {
            const st = state.mapGrid[c]?.[r]?.state
            if (st === 'on' || st === 'done') return true
          }
        }
        return false
      },
    },
    {
      id: 'tp5-2',
      instruction: '2줄×5칸을 드래그해서 10개 묶음을 만들어요!',
      hint: '위 2줄, 왼쪽 5칸을 드래그하세요',
      highlightArea: { type: 'map', startCol: 0, startRow: 0, endCol: 4, endRow: 1 },
      validate: (state) => state.fullCount >= 10,
    },
    {
      id: 'tp5-3',
      instruction: '잘했어요! 나머지도 같은 방법으로 묶어보세요!',
      hint: '다음 2줄×5칸을 드래그하세요',
      validate: (state) => state.completed,
    },
  ],
}

// ============================================================
// fatTie — 뚱십 묶기: 7×4=28
// 텔레포트 없이 직접 뚱십(2×5, 5×2) 묶기를 배운다
// ============================================================
const fatTieModule: TutorialModule = {
  id: 'fatTie',
  name: '뚱십 묶기',
  description: '텔레포트 없이 2×5/5×2 직접 묶기',
  problem: { left: 7, right: 4 },
  steps: [
    {
      id: 'fat-1',
      instruction: '5줄×2칸을 드래그해서 10개 묶음을 만들어요!',
      hint: '위 5줄, 왼쪽 2칸을 세로로 드래그하세요',
      highlightArea: { type: 'map', startCol: 0, startRow: 0, endCol: 1, endRow: 4 },
      validate: (state) => state.fullCount >= 10,
    },
    {
      id: 'fat-2',
      instruction: '잘했어요! 나머지도 같은 방법으로 묶어보세요!',
      hint: '5줄×2칸 또는 2줄×5칸을 드래그하세요',
      validate: (state) => state.completed,
    },
  ],
}

// ============================================================
// bank — 은행: 행/열을 10으로 확장하여 줄십 묶기
//
// 메커니즘:
//   1. 빌리기: 은행에서 N개 꺼냄
//   2. 배치: 맵의 행(또는 열)을 10으로 확장
//   3. 반납: 마지막 행(또는 열)에서 N개를 은행에 돌려줌
//   4. 묶기: 완성된 10줄을 줄십으로 묶기
//
// 예) 6×9=54 → 가로 확장
//   빌림 6개(행 6 × 부족 1) → 6×10=60 → 마지막 행에서 6개 반납
//   → 5×10 + 4 = 54
//
// 예) 7×3=21 → 세로 확장
//   빌림 9개(열 3 × 부족 3) → 3×10=30 → 마지막 열에서 9개 반납
//   → 2×10 + 1 = 21
//
// 공식: 가로 = left × (10-right), 세로 = right × (10-left)
//       은행 용량(11) 이하인 축을 선택
// ============================================================

/** 은행 전략 계산: 확장 축, 필요 개수, 묶음/낱개 결과 */
export function calcBankStrategy(problem: Problem): {
  axis: 'horizontal' | 'vertical'
  needed: number
  perLine: number
  tieCount: number
  loose: number
  total: number
} | null {
  const { left, right } = problem
  const total = left * right

  // 가로 확장: 열(right)을 10으로 → 행마다 (10-right)개 추가
  const hNeeded = right < 10 ? left * (10 - right) : Infinity
  // 세로 확장: 행(left)을 10으로 → 열마다 (10-left)개 추가
  const vNeeded = left < 10 ? right * (10 - left) : Infinity

  const hValid = isFinite(hNeeded) && hNeeded > 0 && hNeeded <= 11
  const vValid = isFinite(vNeeded) && vNeeded > 0 && vNeeded <= 11

  if (!hValid && !vValid) return null

  const useH = hValid && (!vValid || hNeeded <= vNeeded)

  if (useH) {
    return {
      axis: 'horizontal',
      needed: hNeeded,
      perLine: 10 - right,   // 행마다 추가할 개수
      tieCount: left - 1,    // 묶을 줄 수 (마지막 행은 반납용)
      loose: 10 - hNeeded,   // 마지막 행 반납 후 남는 낱개
      total,
    }
  }
  return {
    axis: 'vertical',
    needed: vNeeded,
    perLine: 10 - left,     // 열마다 추가할 개수
    tieCount: right - 1,    // 묶을 줄 수 (마지막 열은 반납용)
    loose: 10 - vNeeded,    // 마지막 열 반납 후 남는 낱개
    total,
  }
}

/** 수식을 받아 동적 은행 튜터리얼 모듈 생성 */
export function createBankModule(problem: Problem = { left: 7, right: 3 }): TutorialModule {
  const strategy = calcBankStrategy(problem)
  const total = problem.left * problem.right

  // 은행 전략 없음 — 이 수식에는 은행이 적합하지 않음
  if (!strategy) {
    return {
      id: 'bank',
      name: '은행',
      description: `${problem.left}×${problem.right}=${total} — 은행 사용 불가 (필요 개수 > 11)`,
      problem,
      steps: [
        {
          id: 'bank-na',
          instruction: `${problem.left}×${problem.right}=${total}은 10으로 확장하려면 은행(최대 11개)이 부족합니다. 다른 방법을 사용하세요.`,
          hint: '뚱십 또는 텔레포트를 사용해보세요',
          validate: () => false,
        },
      ],
    }
  }

  const { axis, needed } = strategy
  const isH = axis === 'horizontal'
  const lineLabel = isH ? '행' : '열'
  const targetLabel = isH ? '가로' : '세로'

  return {
    id: 'bank',
    name: '은행',
    description: `${problem.left}×${problem.right}=${total} — 은행 ${needed}개로 ${targetLabel} 10 확장`,
    problem,
    steps: [
      {
        id: 'bank-1',
        instruction: `은행에서 ${needed}개를 꺼내보세요!`,
        hint: `은행 영역에서 ${needed}개를 드래그하세요`,
        highlightArea: { type: 'bank', startCol: 0, startRow: 0, endCol: 3, endRow: 2 },
        validate: (state) => {
          for (let c = 0; c < 4; c++) {
            for (let r = 0; r < 3; r++) {
              if (state.bankGrid[c]?.[r]?.isEmpty) continue
              const st = state.bankGrid[c]?.[r]?.state
              if (st === 'select' || st === 'off') return true
            }
          }
          return false
        },
      },
      {
        id: 'bank-2',
        instruction: `빈칸에 인형을 놓아 ${targetLabel} 10칸으로 만드세요!`,
        hint: '그리드의 빈칸을 드래그하세요',
        highlightArea: { type: 'map' },
        validate: (state) => {
          for (let c = 0; c < 4; c++) {
            for (let r = 0; r < 3; r++) {
              if (state.bankGrid[c]?.[r]?.isEmpty) continue
              if (state.bankGrid[c]?.[r]?.state === 'off') return true
            }
          }
          return false
        },
      },
      {
        id: 'bank-3',
        instruction: `마지막 ${lineLabel}에서 ${needed}개를 은행에 돌려주세요!`,
        hint: `마지막 ${lineLabel}의 인형을 드래그하세요`,
        highlightArea: { type: 'bank' },
        validate: (state) => {
          for (let c = 0; c < 4; c++) {
            for (let r = 0; r < 3; r++) {
              if (state.bankGrid[c]?.[r]?.isEmpty) continue
              const st = state.bankGrid[c]?.[r]?.state
              if (st === 'off' || st === 'select') return false
            }
          }
          return true
        },
      },
      {
        id: 'bank-4',
        instruction: `잘했어요! 10개씩 묶어보세요!`,
        hint: `${isH ? '가로' : '세로'} 10개를 드래그하세요`,
        validate: (state) => state.completed,
      },
    ],
  }
}

// 기본 은행 모듈 (7×3, 하위 호환용)
const bankModule: TutorialModule = createBankModule({ left: 7, right: 3 })

// ============================================================
// Registry
// ============================================================
const MODULE_REGISTRY: Record<TutorialModuleId, TutorialModule> = {
  teleport10: teleport10Module,
  teleport5: teleport5Module,
  fatTie: fatTieModule,
  bank: bankModule,
}

/** TutorialModuleId 배열로 실제 모듈 객체 반환. problem 전달 시 bank 모듈을 해당 수식으로 동적 생성 */
export function resolveTutorialModules(ids: TutorialModuleId[], problem?: Problem): TutorialModule[] {
  return ids.map(id => {
    if (id === 'bank' && problem) return createBankModule(problem)
    return MODULE_REGISTRY[id]
  }).filter(Boolean)
}

/** 전체 모듈 목록 (UI에서 선택용) */
export const ALL_TUTORIAL_MODULES: { id: TutorialModuleId; name: string; description: string }[] = [
  { id: 'teleport10', name: '텔레포트(10)', description: '→10 + 줄십 자동 묶기' },
  { id: 'teleport5', name: '텔레포트(5)', description: '→5 + 뚱십 수동 묶기' },
  { id: 'fatTie', name: '뚱십 묶기', description: '2×5/5×2 직접 묶기' },
  { id: 'bank', name: '은행', description: '빌리기/놓기/반납 후 묶기' },
]
